import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement,
  Title, Tooltip, Legend, ArcElement, Filler,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import { getStats } from '@/services/api';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, Filler,
  Title, Tooltip, Legend, ArcElement
);

const formatDate = (isoDate: string | null | undefined): string => {
  if (!isoDate) return '—';
  try {
    const d = new Date(isoDate);
    if (isNaN(d.getTime())) return isoDate;
    return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return isoDate;
  }
};

const getStatusInfo = (status: string | null | undefined) => {
  const s = (status || '').toLowerCase().trim();
  if (s === 'active' || s === 'опубликован' || s === 'действует' || s === 'published') {
    return { label: 'Действует', cssClass: 'active' };
  }
  if (s === 'amended' || s === 'изменён') {
    return { label: 'Изменён', cssClass: 'amended' };
  }
  if (s === 'repealed' || s === 'утратил силу' || s === 'отменён' || s === 'revoked') {
    return { label: 'Утратил силу', cssClass: 'repealed' };
  }
  if (s === 'project' || s === 'проект' || s === 'draft') {
    return { label: 'Проект', cssClass: 'project' };
  }
  return { label: status || 'Неизвестно', cssClass: 'unknown' };
};

const defaultLineLabels = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн'];
const defaultLineData = [120, 190, 150, 220, 180, 240];
const defaultDoughnutLabels = ['Действует', 'Изменён', 'Утратил силу', 'Проект'];
const defaultDoughnutData = [38420, 5240, 3420, 1151];
const defaultDoughnutColors = ['#22c55e', '#f59e0b', '#ef4444', '#94a3b8'];

export const DashboardPage: React.FC = () => {
  const { data: stats, isLoading, error } = useQuery('stats', getStats);

  const lineChartData = React.useMemo(() => {
    if (stats?.monthlyTrend && stats.monthlyTrend.length > 0) {
      const labels = stats.monthlyTrend.map((r: any) => r.month);
      const data = stats.monthlyTrend.map((r: any) => parseInt(r.count, 10));
      return {
        labels,
        datasets: [{
          label: 'Новые документы', data,
          borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.1)',
          fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: '#2563eb',
        }],
      };
    }
    return {
      labels: defaultLineLabels,
      datasets: [{
        label: 'Новые документы', data: defaultLineData,
        borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.1)',
        fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: '#2563eb',
      }],
    };
  }, [stats?.monthlyTrend]);

  const doughnutChartData = React.useMemo(() => {
    if (stats?.statusDistribution && stats.statusDistribution.length > 0) {
      const labels: string[] = []; const data: number[] = []; const bgColors: string[] = [];
      const colorMap: Record<string, string> = { active: '#22c55e', amended: '#f59e0b', repealed: '#ef4444', project: '#94a3b8', unknown: '#64748b' };
      const labelMap: Record<string, string> = { active: 'Действует', amended: 'Изменён', repealed: 'Утратил силу', project: 'Проект', unknown: 'Неизвестно' };
      stats.statusDistribution.forEach((r: any) => {
        const key = r.status_group || 'unknown';
        labels.push(labelMap[key] || key);
        data.push(parseInt(r.count, 10));
        bgColors.push(colorMap[key] || '#64748b');
      });
      return { labels, datasets: [{ data, backgroundColor: bgColors, borderWidth: 0 }] };
    }
    return { labels: defaultDoughnutLabels, datasets: [{ data: defaultDoughnutData, backgroundColor: defaultDoughnutColors, borderWidth: 0 }] };
  }, [stats?.statusDistribution]);

  const lineOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } } };
  const doughnutOptions = { responsive: true, maintainAspectRatio: false, cutout: '70%', plugins: { legend: { position: 'bottom' as const, labels: { usePointStyle: true, padding: 16 } } } };

  const metrics = [
    { icon: 'bi-file-earmark-text', color: '#2563eb', value: (stats?.total ?? 0).toLocaleString(), label: 'Всего документов', delta: `+${(stats?.monthlyGrowth ?? 0).toLocaleString()} за месяц`, up: true },
    { icon: 'bi-check-circle', color: '#22c55e', value: (stats?.active ?? 0).toLocaleString(), label: 'Действующих', delta: `+${(stats?.dailyGrowth ?? 0).toLocaleString()} сегодня`, up: true },
    { icon: 'bi-clock', color: '#f59e0b', value: (stats?.ocrPending ?? 0).toLocaleString(), label: 'Ожидает OCR', delta: '-3 за час', up: false },
    { icon: 'bi-cpu', color: '#8b5cf6', value: (stats?.nlpPending ?? 0).toLocaleString(), label: 'Ожидает NLP', delta: '-5 за час', up: false },
    { icon: 'bi-diagram-3', color: '#ec4899', value: '512,340', label: 'Связей в графе', delta: '+2,100 за день', up: true },
    { icon: 'bi-people', color: '#06b6d4', value: '1,247', label: 'Пользователей', delta: '+12 сегодня', up: true },
  ];

  const recentDocs = stats?.recent || [];

  return (
    <div>
      <h4 className="mb-4 fw-bold">Дашборд</h4>
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Загрузка...</span></div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Ошибка загрузки: {(error as any)?.message || 'Неизвестная ошибка'}
        </div>
      ) : (
        <>
          <div className="row g-3 mb-4">
            {metrics.map((m, i) => (
              <div className="col-xl-2 col-md-4 col-6" key={i}>
                <div className="metric-card">
                  <div className="metric-icon" style={{ background: m.color + '15', color: m.color }}>
                    <i className={`bi ${m.icon}`}></i>
                  </div>
                  <div className="metric-value">{m.value}</div>
                  <div className="metric-label">{m.label}</div>
                  <div className={`metric-delta ${m.up ? 'up' : 'down'}`}>
                    <i className={`bi bi-arrow-${m.up ? 'up' : 'down'}-short`}></i>{m.delta}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row g-3 mb-4">
            <div className="col-lg-8">
              <div className="chart-container" style={{ height: '300px' }}>
                <h6 className="fw-bold mb-3">Динамика публикаций</h6>
                <div style={{ height: '240px' }}><Line data={lineChartData} options={lineOptions} /></div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="chart-container" style={{ height: '300px' }}>
                <h6 className="fw-bold mb-3">Распределение по статусам</h6>
                <div style={{ height: '240px' }}><Doughnut data={doughnutChartData} options={doughnutOptions} /></div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-lg-8">
              <div className="table-card">
                <div className="table-card-header">
                  <h5>Последние документы</h5>
                  <Link to="/timeline" className="btn btn-sm btn-outline-primary">Все</Link>
                </div>
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr><th>№ EO</th><th>Название</th><th>Тип</th><th>Орган</th><th>Дата</th><th>Статус</th></tr>
                    </thead>
                    <tbody>
                      {recentDocs.length === 0 && (
                        <tr><td colSpan={6} className="text-center text-muted py-5">Нет документов</td></tr>
                      )}
                      {recentDocs.map((doc: any) => {
                        const statusInfo = getStatusInfo(doc.status);
                        return (
                          <tr key={doc.id}>
                            <td className="font-monospace small">{doc.eo_number}</td>
                            <td><Link to={`/document/${doc.id}`} className="text-decoration-none">{doc.title}</Link></td>
                            <td>{doc.doc_type || '—'}</td>
                            <td>{doc.authority || '—'}</td>
                            <td>{formatDate(doc.doc_date || doc.view_date)}</td>
                            <td>
                              <span className={`badge-status badge-${statusInfo.cssClass}`}>{statusInfo.label}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="table-card">
                <div className="table-card-header"><h5>Лог ingestion</h5></div>
                <div className="table-responsive">
                  <table className="table">
                    <thead><tr><th>Действие</th><th>EO</th><th>Статус</th><th>Время</th></tr></thead>
                    <tbody>
                      {[
                        { action: 'download', eo: 'EO-892', status: 'success', time: '2 мин назад' },
                        { action: 'download', eo: 'EO-156-ФЗ', status: 'success', time: '15 мин назад' },
                        { action: 'ocr', eo: 'EO-342н', status: 'processing', time: '18 мин назад' },
                        { action: 'nlp', eo: 'EO-891', status: 'processing', time: '32 мин назад' },
                        { action: 'index', eo: 'EO-155-ФЗ', status: 'success', time: '1 ч назад' },
                      ].map((log, i) => (
                        <tr key={i}>
                          <td><span className="badge bg-light text-dark">{log.action}</span></td>
                          <td className="font-monospace small">{log.eo}</td>
                          <td>
                            <span className={`badge-status badge-${log.status}`}>
                              {log.status === 'success' ? 'Успешно' : 'В процессе'}
                            </span>
                          </td>
                          <td className="text-muted small">{log.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
